// @flow

import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { injectStripe } from 'react-stripe-elements';
import Fieldset, { Error } from '@clark-inc/clark-forms';
import { formatCentsToDollars } from 'clark-utils';
import isEmpty from 'lodash/isEmpty';

import BankAccountDetailsForm from 'components/payer/bank-account-details-form';
import CreditCardDetailsForm from 'components/payer/credit-card-details-form';

import Layout from 'components/shared/layout';
import Button from 'components/shared/button';
import TermsAndServices from 'components/terms-and-services';
import FaqLink from 'components/faq-link';
import Lock from 'assets/icons/lock.svg';
import { getBusiness } from 'utils/user-auth';

import {
  TERMS_FIELD_SET,
  SUBMIT_INFO,
  RECEIPT_FIELD,
  AUTOPAY_FIELD,
  FAQ_COPY,
} from 'constants/payer/charge';

import {
  CARD_ELEMENT_OPTIONS,
  ButtonWrapper,
  CardCVC,
  CardExpiry,
  CardNumber,
  FieldContent,
  FieldsetContainer,
  FieldsetContent,
  Form,
  Label,
  PostalCode,
  SubmitContainer,
  SubmitInfo,
  SubmitInfoHeadline,
  SubText,
  AgreeToTerms,
  AgreeToTermsText,
  Link,
} from './styles';

const mapStateToProps = ({ payer: { paymentMethod } }) => ({
  paymentMethod,
});

const mapDispatchToProps = {};

class ChargeForm extends Component<*, *> {
  constructor() {
    super();
    this.state = {
      cardNumber: { msg: '', valid: false },
      cardExpiry: { msg: '', valid: false },
      cardCVC: { msg: '', valid: false },
      postalCode: { msg: '', valid: false },
    };
  }

  handleChange = type => ({ error, complete }) => {
    if (error) {
      this.setState(prevState => ({
        [type]: {
          ...prevState[type],
          msg: error.message,
        },
      }));
    } else {
      this.setState(prevState => ({
        [type]: {
          ...prevState[type],
          valid: complete,
          msg: '',
        },
      }));
    }
  };

  render() {
    const {
      handlePlaidSuccess,
      handleSubmit,
      handleClick,
      invalid,
      stripe,
      charge = {},
      business = {
        attributes: {},
      },
      paymentMethod,
      pendingBankAccountAttributes,
      toggleCreditCardForm,
      showNewCreditCardForm,
      submitting,
      submitSucceeded,
    } = this.props;

    const { clientTermsUrl, forceAutopay } = getBusiness();

    const {
      cardNumber: { msg: cardNumberMsg, valid: cardNumberValid },
      cardExpiry: { msg: cardExpiryMsg, valid: cardExpiryValid },
      cardCVC: { msg: cardCVCMsg, valid: cardCVCValid },
      postalCode: { msg: postalCodeMsg, valid: postalCodeValid },
    } = this.state;

    const { creditCard: CREDIT_CARD_FAQ } = FAQ_COPY;
    const { attributes: { totalAmountInCents, paymentMethodType } = {} } = charge;

    const isPaymentMethodCreditCard =
      !charge || (paymentMethodType === 'creditCard' || !paymentMethodType);
    const isPaymentMethodBankAccount = paymentMethodType === 'bankAccount';

    let hasBankAccountInfo;
    if (paymentMethod) {
      const {
        attributes: { bankAccountLast4 },
      } = paymentMethod;
      hasBankAccountInfo = bankAccountLast4;
    }

    const bankAccountSubmitEnabled =
      isPaymentMethodBankAccount && (hasBankAccountInfo || pendingBankAccountAttributes);

    const creditCardSubmitEnabled =
      (cardNumberValid && cardExpiryValid && cardCVCValid && postalCodeValid) || paymentMethod;

    const submitEnabled =
      bankAccountSubmitEnabled ||
      (isPaymentMethodCreditCard && creditCardSubmitEnabled && !invalid);

    return (
      <Fragment>
        <Form onSubmit={handleSubmit(() => handleClick(stripe))}>
          {showNewCreditCardForm && (
            <Layout>
              <FieldContent>
                <Label htmlFor="CardNumberElement">Card Number</Label>
                <CardNumber
                  showError={cardNumberMsg}
                  onChange={this.handleChange('cardNumber')}
                  {...CARD_ELEMENT_OPTIONS}
                />
                <Error touched error={cardNumberMsg} />
              </FieldContent>
              <FieldsetContainer>
                <FieldsetContent>
                  <Label htmlFor="CardExpiryElement"> Expires</Label>
                  <CardExpiry
                    showError={cardExpiryMsg}
                    onChange={this.handleChange('cardExpiry')}
                    {...CARD_ELEMENT_OPTIONS}
                  />
                  <Error touched error={cardExpiryMsg} />
                </FieldsetContent>
                <FieldsetContent>
                  <Label htmlFor="CardCVCElement">CVC</Label>
                  <CardCVC
                    showError={cardCVCMsg}
                    onChange={this.handleChange('cardCVC')}
                    {...CARD_ELEMENT_OPTIONS}
                  />
                  <Error touched error={cardCVCMsg} />
                </FieldsetContent>
                <FieldsetContent>
                  <Label htmlFor="PostalCodeElement">Postal Code</Label>
                  <PostalCode
                    showError={postalCodeMsg}
                    onChange={this.handleChange('postalCode')}
                    {...CARD_ELEMENT_OPTIONS}
                  />
                  <Error touched error={postalCodeMsg} />
                </FieldsetContent>
              </FieldsetContainer>
              <SubText>
                <FaqLink icon={<Lock />} copy={CREDIT_CARD_FAQ.copy} link={CREDIT_CARD_FAQ.link} />
              </SubText>
              <Fieldset data={RECEIPT_FIELD} />
              {!forceAutopay && <Fieldset data={AUTOPAY_FIELD} />}
            </Layout>
          )}
          <SubmitContainer>
            <Layout>
              {!isEmpty(charge) && (
                <SubmitInfo>
                  <SubmitInfoHeadline>{SUBMIT_INFO.headline}</SubmitInfoHeadline>

                  {isPaymentMethodBankAccount && (
                    <BankAccountDetailsForm
                      pendingBankAccountAttributes={pendingBankAccountAttributes}
                      paymentMethod={paymentMethod}
                      business={business}
                      handlePlaidSuccess={handlePlaidSuccess}
                    />
                  )}

                  {isPaymentMethodCreditCard && (
                    <CreditCardDetailsForm
                      paymentMethod={paymentMethod}
                      onClickReplace={toggleCreditCardForm}
                    />
                  )}
                </SubmitInfo>
              )}

              {clientTermsUrl && (
                <AgreeToTerms>
                  <Fieldset data={TERMS_FIELD_SET} />
                  <AgreeToTermsText>
                    I agree to the{' '}
                    <Link href={clientTermsUrl} target="_blank" rel="noopener noreferrer">
                      Terms and Conditions
                    </Link>
                    .
                  </AgreeToTermsText>
                </AgreeToTerms>
              )}

              <ButtonWrapper>
                <Button
                  disabled={!submitEnabled || submitting || submitSucceeded}
                  layout="secondary"
                >
                  {isEmpty(charge)
                    ? 'Add Payment'
                    : `Pay ${formatCentsToDollars(totalAmountInCents)}`}
                </Button>
              </ButtonWrapper>
              {!clientTermsUrl && <TermsAndServices />}
            </Layout>
          </SubmitContainer>
        </Form>
      </Fragment>
    );
  }
}

export default injectStripe(
  reduxForm({
    form: 'payerCharge',
    destroyOnUnmount: false,
  })(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(ChargeForm),
  ),
);



// WEBPACK FOOTER //
// ./src/containers/payer/charge-form/index.js